import { CircularProgress } from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { debounce } from "@mui/material/utils";
import Box from "@mui/system/Box";
import differenceInCalendarDays from "date-fns/fp/differenceInCalendarDays";
import type { NextPage } from "next";
import { useRouter } from "next/dist/client/router";
import Head from "next/head";
import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import DashboardBriefTable from "src/components/dashboard/dashboard-brief-table";
import DeleteDialog from "src/components/dashboard/deleteConfirmationDialog";
import { useAuth } from "src/hooks/use-auth";
import { getActivePlan } from "src/slices/auth/thunks";
import { selectBrief, setFakeCreatedAt } from "src/slices/brief";
import {
  briefsRequested as activeBriefsRequested,
  archivedBriefsRequested,
  checkBriefStatus,
  retryBrief,
} from "src/slices/brief/thunks";
import { BriefType } from "src/slices/brief/types";
import { selectCurrentPlan } from "src/slices/system/selectors";
import { AuthGuard } from "../../components/authentication/auth-guard";
import { CreateBriefDialog } from "../../components/dashboard/createBriefDialog";
import { DashboardLayout } from "../../components/dashboard/dashboard-layout";
import { SelectProjectDropdown } from "../../components/dashboard/projects/SelectProjectDropdown";
//optimizeContentDialog
import {
  aiWordsUpgradeModalShowed,
  upgradeModalShowed,
} from "src/slices/system";
import { useDispatch } from "src/store";

import { OptimizeContentDialog } from "src/components/dashboard/optimizeContent/optimizeContentDialog";
import { gtm } from "../../lib/gtm";

import { AddPaymentModal } from "../../components/dashboard/addPaymenModal";
import { Onboarding } from "../../components/dashboard/onboarding";

import {
  generateArticle,
  getArticle,
  getUserDetails,
} from "src/slices/articles/thunks";
import { selectSelectedOutlines } from "src/slices/brief";
import { getProjectById } from "src/slices/projects/selectors";
import { selectLoaders } from "src/slices/suggestions/selectors";
import { snackAdded } from "src/slices/system";
import { getCurrentUser } from "src/slices/system/thunks";
import { store } from "src/store";

// Import the new utility functions
import {
  ArticleProgressType,
  BriefProgressType,
  cleanupSubscriptions,
  subscribeToArticleStatus,
  subscribeToBriefStatus,
} from "src/utils/appsync";

// Add these types at the top of the file
type ArticleStatusData = {
  id: string;
  status: boolean;
  current: number;
  total: number;
  percentage: number;
  title: string;
  content: string;
};

// Add the Brief status type
type BriefStatusData = {
  id: string;
  stepRemains: number;
};

const Overview: NextPage = () => {
  const [archivedPage, setArchivedPage] = useState<number>(0);
  const [briefModalLoading, setBriefModalLoading] = useState<boolean>(false);
  const [optimizeContentDialogLoading, setOptimizeContentDialogLoading] =
    useState<boolean>(false);
  const [tab, setTab] = React.useState("active");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState("");
  const [query, setQuery] = useState<string>("");
  const [currentProject, setCurrentProject] = useState<string>("All");
  const [isBriefDialogOpen, setIsBriefDialogOpen] = useState(false);
  const [isOptimizeContentDialogOpen, setIsOptimizeContentDialogOpen] =
    useState(false);
  const [optimizeContentPrefillData, setOptimizeContentPrefillData] = 
    useState<any>(null);
  const [isDialogTransitioning, setIsDialogTransitioning] = useState(false);

  const [briefLimit, setBriefLimit] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [page, setPage] = useState(0);
  const router = useRouter();
  const { user } = useAuth();
  const [isOnboardingShown, setIsOnboardingShown] = useState(true);
  const dispatch = useDispatch();

  const {
    entities,

    total,
    loading,
    archivedEntities,
    archivedTotal,
  } = useSelector(selectBrief);
  const suggestionLoaders = useSelector(selectLoaders);
  const [activePageLimit, setActivePageLimit] = useState(25);
  const [archivedPageLimit, setArchivedPageLimit] = useState(25);
  const [searchIntentUrls, setSearchIntentUrls] = useState<string[]>([]);

  const selectedOutlines = useSelector(selectSelectedOutlines);

  // Add state to track processing briefs
  const [processingBriefIds, setProcessingBriefIds] = useState([]);
  const [inProgressBriefIds, setInProgressBriefIds] = useState([]);

  // Subscription reference to clean up on unmount
  const subscriptionRef = useRef(null);

  // Add a state to track article progress
  const [articleProgress, setArticleProgress] = useState<
    Record<string, ArticleProgressType>
  >({});

  // Add a state to track brief progress
  const [briefProgress, setBriefProgress] = useState<
    Record<string, BriefProgressType>
  >({});

  // Add this ref to track active subscriptions
  const activeSubscriptions = useRef(new Map());
  const activeBriefSubscriptions = useRef(new Map());

  // Function to combine unique similarity mappings
  const combineUniqueSimilarityMappings = (selectedOutlines: any[]) => {
    const uniqueValues = new Set<string>(); // Use a Set to store unique values

    selectedOutlines.forEach((outline) => {
      const similarityMapping = outline.similarityMapping as Record<
        string,
        string
      >;

      // Skip this outline if it has no similarity mapping
      if (!similarityMapping) {
        return;
      }

      // Iterate through the similarity mapping
      for (const value of Object.values(similarityMapping)) {
        if (typeof value === "string" && value) {
          uniqueValues.add(value); // Add the value to the Set
        }
      }
    });
    // if uniqueValues is less then 3, top up from the urls each outline was
    // matched against. The field is `urls` (an array) - there is no `url`
    // field, so the previous `outline.url` lookup only ever added `undefined`,
    // which reached the article worker as null and failed the run.
    if (uniqueValues.size < 3) {
      selectedOutlines.forEach((outline) => {
        (outline.urls || []).forEach((url: string) => {
          if (typeof url === "string" && url) {
            uniqueValues.add(url);
          }
        });
      });
    }

    return Array.from(uniqueValues); // Convert the Set back to an array
  };

  const briefs = entities?.map((brief) => ({
    ...brief,
    status:
      typeof brief.jobId === "undefined" &&
      brief.status !== "Extracting Keywords"
        ? "Retrying"
        : brief.status,
  }));

  const activeBriefs = briefs;
  const archivedBriefs = archivedEntities;

  const areBriefsLoading = loading["briefs/requested"] === "pending";
  const currentPlan: any = useSelector(selectCurrentPlan);
  const intervalRef = useRef(null);
  const briefReloadIntervalRef = useRef(null);

  // Sid TODO: Remove this after testing
  // const isBriefLimitReached = currentPlan?.limitReached;

  const isArchiveTab = tab === "archived";
  const briefsRequested = isArchiveTab
    ? archivedBriefsRequested
    : activeBriefsRequested;

  useEffect(() => {
    gtm.push({ event: "page_view" });
  }, []);

  // refactor code of briefsRequested start

  const fetchBriefs = (
    isArchiveTab: boolean,
    archivedPageValue: any = archivedPage,
    activePageValue: any = page,
    setPageLimit: number = activePageLimit,
    setArchivedPageLimit: number = archivedPageLimit,
    isInitialLoad: boolean = false
  ) => {
    dispatch(
      briefsRequested(
        isArchiveTab
          ? {
              limit: setArchivedPageLimit,
              page: archivedPageValue,

              keyword: query,
              project: currentProject,
            }
          : {
              limit: setPageLimit,
              page: activePageValue,
              keyword: query,
              project: currentProject,
            }
      )
    );

    if (isInitialLoad) {
      dispatch(
        archivedBriefsRequested({
          limit: archivedPageLimit,
          keyword: query,
          page: archivedPageValue,
          project: currentProject,
        })
      );
    }
  };

  useEffect(() => {
    const fetchData = () => {
      fetchBriefs(
        isArchiveTab,
        archivedPage,
        page,
        activePageLimit,
        archivedPageLimit,
        true
      );
    };

    fetchData(); // Fetch data on mount

    const handleRouteChange = () => {
      clearTimeout(briefReloadIntervalRef.current);
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      clearTimeout(briefReloadIntervalRef.current); // Ensure timeout is cleared on unmount
    };
  }, [
    isArchiveTab,
    archivedPage,
    page,
    activePageLimit,
    archivedPageLimit,
    query,
    router.events,
    currentProject,
  ]); // Added necessary dependencies

  useEffect(() => {
    const checkProcessingStatus = () => {
      let processingBriefs = false;
      let isBriefGettingProcessed = false;

      // New arrays to track which briefs are processing
      let processingBriefIds = [];
      let inProgressBriefIds = [];

      if (isArchiveTab) {
        // Find all processing briefs
        archivedBriefs?.forEach((brief) => {
          if (brief.articleStatus === "processing") {
            processingBriefIds.push(brief.id);
          }
          if (brief?.stepRemains !== undefined && brief?.stepRemains > 0) {
            inProgressBriefIds.push(brief.id);
          }
        });

        processingBriefs = processingBriefIds.length > 0;
        isBriefGettingProcessed = inProgressBriefIds.length > 0;
      } else {
        // Find all processing briefs
        briefs?.forEach((brief) => {
          if (brief.articleStatus === "processing") {
            processingBriefIds.push(brief.id);
          }
          if (brief?.stepRemains !== undefined && brief?.stepRemains > 0) {
            inProgressBriefIds.push(brief.id);
          }
        });

        processingBriefs = processingBriefIds.length > 0;
        isBriefGettingProcessed = inProgressBriefIds.length > 0;
      }

      if (briefReloadIntervalRef.current) {
        clearTimeout(briefReloadIntervalRef.current);
      }

      // if (processingBriefs || isBriefGettingProcessed) {
      //   briefReloadIntervalRef.current = setTimeout(() => {
      //     setPage(0);
      //     setArchivedPage(0);
      //     setActivePageLimit(25);
      //     setArchivedPageLimit(25);

      //     fetchBriefs(
      //       isArchiveTab,
      //       archivedPage,
      //       page,
      //       activePageLimit,
      //       archivedPageLimit
      //     );

      //     // Clear the timeout
      //     briefReloadIntervalRef.current = null;
      //   }, 150000);
      // }
    };

    checkProcessingStatus(); // Call the function to check status on mount
  }, [activeBriefs, archivedBriefs, isArchiveTab]); // Added necessary dependencies

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setQuery(event?.target?.value);
    setPage(0);
    setArchivedPage(0);
  };
  const debouncedChangeHandler = useCallback(
    debounce(handleQueryChange, 500),
    []
  );

  const handlePageChange = (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    isArchiveTab ? setArchivedPage(newPage) : setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    setPage(0);
    setArchivedPage(0);
    setActivePageLimit(Number(event.target.value));
    setArchivedPageLimit(Number(event.target.value));
  };

  // const checkBriefStatusRecursive = async (briefId) => {
  //   try {
  //     let res = await dispatch(
  //       checkBriefStatus({
  //         id: briefId,
  //       })
  //     ).unwrap();

  //     // Check if response exists before proceeding
  //     if (!res) {
  //       return;
  //     }

  //     //update stepRemains in the brief
  //     if (res?.stepRemains !== 0 && !res?.retry) {
  //       setTimeout(() => {
  //         checkBriefStatusRecursive(res?.id);
  //       }, 25000);
  //     } else if (
  //       res?.stepRemains === 0 &&
  //       res?.generateArticle === true &&
  //       res?.articleStatus === undefined &&
  //       !res?.retry
  //     ) {
  //       setTimeout(() => {
  //         checkBriefStatusRecursive(res?.id);
  //       }, 10000);
  //     }
  //   } catch (error) {
  //     // Don't try to continue with recursive calls if there was an error
  //   }
  // };

  const handleCloseBriefDialog = (briefId) => {
    // checkBriefStatusRecursive(briefId);
    setIsBriefDialogOpen(false);
  };

  const handleOnboardingClose = (briefId) => {
    setIsOnboardingShown(true);
  };

  const handleCloseOptimizeContentDialog = () => {
    setIsOptimizeContentDialogOpen(false);
    setOptimizeContentPrefillData(null);
  };

  const handleOptimizeContentFromBrief = (data: any) => {
    setOptimizeContentPrefillData(data);
    setIsDialogTransitioning(true);
    
    // First, close the Brief dialog with fade out animation
    setIsBriefDialogOpen(false);
    
    // After a delay, open the Optimize Content dialog with zoom in animation
    setTimeout(() => {
      setIsOptimizeContentDialogOpen(true);
      setIsDialogTransitioning(false);
    }, 300);
  };

  const handleCreateBriefDialogOpen = async () => {
    setBriefModalLoading(true);
    setIsBriefDialogOpen(false);
    const planDetails = (await dispatch(getActivePlan())) as any;
    let plan = planDetails?.payload?.plan ?? undefined;
    if (
      plan === undefined ||
      Object.keys(plan).length === 0 ||
      planDetails?.payload?.error
    ) {
      setShowPaymentModal(true);
      setBriefModalLoading(false);
      return;
    }
    const isBriefLimitReached = planDetails.payload?.plan?.limitReached;
    setBriefModalLoading(false);
    if (isBriefLimitReached) {
      setBriefLimit(true);
      dispatch(upgradeModalShowed(true));
    } else {
      setBriefLimit(false);
      setIsBriefDialogOpen(true);
    }
  };

  const checArticleLimit = async () => {
    // Refresh the current plan
    const currentPlan = (await dispatch(getActivePlan())).payload?.plan;
    const numberOfAIWords = Number(currentPlan?.numberOfWordsInPlan || 0);
    const numberOfArticles = Number(currentPlan?.numberOfArticlesInPlan || 0);
    let aiWordsLeft = numberOfAIWords - (currentPlan?.totalWordsWritten || 0);
    let articlesLeft =
      numberOfArticles - (currentPlan?.totalArticlesWritten || 0);
    if (aiWordsLeft < 0) {
      aiWordsLeft = 0;
    }
    if (articlesLeft < 0) {
      articlesLeft = 0;
    }

    if (aiWordsLeft === 0 || articlesLeft === 0) {
      let text = "";
      if (articlesLeft === 0) {
        text = `You've reached a limit for Updating Articles`;
      } else {
        text = ` You've reached a limit of word count`;
      }

      dispatch(aiWordsUpgradeModalShowed(text));
      return true;
    }
    return false;
  };

  const handleOptimizeContentDialogOpen = async () => {
    setOptimizeContentDialogLoading(true);
    setIsOptimizeContentDialogOpen(false);
    const planDetails = (await dispatch(getActivePlan())) as any;
    let plan = planDetails?.payload?.plan ?? undefined;
    if (
      plan === undefined ||
      Object.keys(plan).length === 0 ||
      planDetails?.payload?.error
    ) {
      setShowPaymentModal(true);
      setOptimizeContentDialogLoading(false);
      return;
    }
    const isArticleLimitReached = await checArticleLimit();
    setOptimizeContentDialogLoading(false);
    if (!isArticleLimitReached) {
      setIsOptimizeContentDialogOpen(true);
    }
  };
  const handleReturnBriefId = (briefId: string) => {};

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setTab(newValue);
  };

  const resetAt =
    currentPlan?.next_bill_date ?? currentPlan?.subscriptionResetAt * 1000;

  const daysToReset = differenceInCalendarDays(new Date(), new Date(resetAt));

  const handleRetry = async ({
    keyword,
    id,
    locationName,
    languageCode,
  }: Omit<BriefType, "status">) => {
    //update the created at to current time

    await dispatch(
      setFakeCreatedAt({ id, createdAt: new Date().toISOString() })
    );

    await dispatch(
      retryBrief({
        id: id,
      })
    );
    //call the checkBriefStatusRecursive
    // checkBriefStatusRecursive(id);
  };

  useEffect(() => {
    const getUser = async () => {
      try {
        const res = await dispatch(getCurrentUser());

        // Only show onboarding if explicitly set to true in user data
        if (res?.payload?.isOnboardingShown === undefined) {
          setIsOnboardingShown(true);
        } else {
          setIsOnboardingShown(res?.payload?.isOnboardingShown);
        }
      } catch (error) {
        setIsOnboardingShown(false); // Keep hidden on error
      }
    };
    getUser();
  }, []);
  const handleDelete = (id: string) => {
    setIsDeleteDialogOpen(id);
  };

  //retry article
  // Article Stuff
  const checkNumberOfH2 = (outline) => {
    //check no of h2 in brief.selectedOutlines
    let h2Count = 0;
    outline.forEach((outline) => {
      if (outline.tag === "H2") {
        h2Count++;
      }
      if (h2Count >= 1) return;
    });
    return h2Count;
  };
  const getProjectDetails = (projectId) => {
    return getProjectById(projectId)(store.getState());
  };
  const handleRetryArticle = async (id) => {
    const brief = activeBriefs.find((brief) => brief.id === id) as BriefType;

    if (!brief.title) {
      dispatch(
        snackAdded({
          message: "Please enter a title to generate an article.",
          type: "error",
          duration: 2500,
        })
      );
      return;
    }
    //if (brief.selectedTopics.length === 0) {
    //  dispatch(
    //    snackAdded({
    //      message: "Please add at least one keyword to generate an article.",
    //      type: "error",
    //      duration: 2500,
    //    })
    //  );
    //  return;
    //}
    if (brief.selectedOutlines.length === 0) {
      dispatch(
        snackAdded({
          message: "Please add outline to generate an article.",
          type: "error",
          duration: 2500,
        })
      );
      return;
    } else if (checkNumberOfH2(brief.selectedOutlines) === 0) {
      dispatch(
        snackAdded({
          message: "Please add at least one H2 to generate an article.",
          type: "error",
          duration: 2500,
        })
      );
      return;
    }
    const project = getProjectDetails(brief.projectId);

    const userDetails = (await dispatch(getUserDetails())).payload?.user;

    // Refresh the current plan
    const currentPlan = (await dispatch(getActivePlan())).payload?.plan;
    if (
      currentPlan &&
      Object.keys(currentPlan).length === 0 &&
      brief?.isOnboardingBrief
    ) {
      setShowPaymentModal(true);

      return;
    }

    const numberOfAIWords = Number(currentPlan?.numberOfWordsInPlan || 0);
    const numberOfArticles = Number(currentPlan?.numberOfArticlesInPlan || 0);
    let aiWordsLeft = numberOfAIWords - (currentPlan?.totalWordsWritten || 0);
    let articlesLeft =
      numberOfArticles - (currentPlan?.totalArticlesWritten || 0);
    if (aiWordsLeft < 0) {
      aiWordsLeft = 0;
    }
    if (articlesLeft < 0) {
      articlesLeft = 0;
    }

    if (aiWordsLeft === 0 || articlesLeft === 0) {
      let text = "";
      if (articlesLeft === 0) {
        text = `You've reached a limit of AI articles`;
      } else {
        text = ` You've reached a limit of word count`;
      }

      dispatch(aiWordsUpgradeModalShowed(text));
      return;
    }

    await dispatch(
      generateArticle({
        briefId: brief.id,
        title: brief.title,
        keyword: brief.keyword,
        selectedTopics: brief.selectedTopics,
        selectedOutlines: brief.selectedOutlines,
        userId: userDetails.Username,
        aiWordsLeft,
        language: brief.languageCode,
        location: brief.locationName,
        addCompanyInfo: brief.companyDetails,
        addInsights: brief.insights.length > 0 ? true : false,
        projectDomain: project?.projectDomain,
        addInterlinking: brief.interlinking,
        GSCsiteUrl: project?.GSCsiteUrl,
        humanize: brief.humanize,
        thoughtLeadership: brief.thoughtLeadership,
        aiResearchAgent: brief.aiResearchAgent,
        aiEvalRefineAgent: brief.aiEvalRefineAgent,
        searchIntentUrls: combineUniqueSimilarityMappings(
          brief.selectedOutlines
        ),
      })
    );

    fetchBriefs(
      isArchiveTab,
      archivedPage,
      page,
      activePageLimit,
      archivedPageLimit
    );
  };

  // Update the useEffect subscription code
  const initialFetchedBriefs = useRef(new Set());
  // Add a new ref to track initially fetched brief status
  const initialFetchedBriefStatus = useRef(new Set());

  useEffect(() => {
    // Only set up subscriptions if user is logged in and there are briefs
    if (!briefs?.length) return;

    // Get briefs with processing articles
    const activeBriefsWithProcessingArticles =
      (isArchiveTab ? archivedBriefs : briefs)?.filter(
        (brief) => brief.articleStatus === "processing"
      ) || [];

    // Get briefs with remaining steps (in progress)
    const activeBriefsWithRemainingSteps =
      (isArchiveTab ? archivedBriefs : briefs)?.filter(
        (brief) => brief.stepRemains > 0
      ) || [];

    // Clean up article subscriptions for briefs that are no longer processing
    activeSubscriptions.current.forEach((subscription, briefId) => {
      if (
        !activeBriefsWithProcessingArticles.find(
          (brief) => brief.id === briefId
        )
      ) {
        subscription.unsubscribe();
        activeSubscriptions.current.delete(briefId);
      }
    });

    // Clean up brief subscriptions for briefs that no longer have remaining steps
    activeBriefSubscriptions.current.forEach((subscription, briefId) => {
      if (
        !activeBriefsWithRemainingSteps.find((brief) => brief.id === briefId)
      ) {
        subscription.unsubscribe();
        activeBriefSubscriptions.current.delete(briefId);
      }
    });

    // Set up article subscription for each processing brief that doesn't already have one
    activeBriefsWithProcessingArticles.forEach((brief) => {
      // Skip if subscription already exists
      if (activeSubscriptions.current.has(brief.id)) {
        return;
      }

      // Initialize progress data for new processing briefs with default values
      setArticleProgress((prev) => {
        if (!prev[brief.id]) {
          return {
            ...prev,
            [brief.id]: {
              status: "processing",
              current: 0,
              total: 100,
              percentage: 0,
            },
          };
        }
        return prev;
      });

      // Fetch the latest status from the database ONLY ONCE per brief
      if (!initialFetchedBriefs.current.has(brief.id)) {
        initialFetchedBriefs.current.add(brief.id);

        dispatch(getArticle({ id: brief.id }))
          .then((result) => {
            const data = result.payload?.data;

            if (data) {
              setArticleProgress((prev) => ({
                ...prev,
                [brief.id]: {
                  status: data.status,
                  current: parseInt(String(data.current || 0), 10),
                  total: parseInt(String(data.total || 100), 10),
                  percentage: parseInt(String(data.percentage || 0), 10),
                },
              }));
            }
          })
          .catch((error) => {
            console.error("Error fetching article status:", error);
          });
      }

      const subscription = subscribeToArticleStatus(
        brief.id,
        // Handle data
        (data) => {
          if (data.value?.data?.onArticleStatusChanged) {
            const statusData = data.value.data.onArticleStatusChanged;

            setArticleProgress((prev) => ({
              ...prev,
              [brief.id]: {
                status: statusData.status,
                current: parseInt(String(statusData.current || 0), 10),
                total: parseInt(String(statusData.total || 100), 10),
                percentage: parseInt(String(statusData.percentage || 0), 10),
              },
            }));
          }
        },
        // Handle error
        (error) => {},
        // Handle complete
        () => {}
      );

      if (subscription) {
        activeSubscriptions.current.set(brief.id, subscription);
      }
    });

    // Delay brief subscriptions to ensure they don't conflict with article subscriptions
    setTimeout(() => {
      // Set up brief subscription for each brief with remaining steps
      activeBriefsWithRemainingSteps.forEach((brief) => {
        // Skip if subscription already exists
        if (activeBriefSubscriptions.current.has(brief.id)) {
          return;
        }

        // Initialize brief progress data
        setBriefProgress((prev) => {
          if (!prev[brief.id]) {
            return {
              ...prev,
              [brief.id]: {
                stepRemains: brief.stepRemains || 0,
                articleStatus: brief.articleStatus || "",
              },
            };
          }
          return prev;
        });

        // Fetch the latest brief status from the database ONLY ONCE per brief
        if (!initialFetchedBriefStatus.current.has(brief.id)) {
          initialFetchedBriefStatus.current.add(brief.id);

          dispatch(checkBriefStatus({ id: brief.id }))
            .then((result) => {
              const data = result.payload;

              if (data) {
                setBriefProgress((prev) => ({
                  ...prev,
                  [brief.id]: {
                    stepRemains: parseInt(String(data.stepRemains || 0), 10),
                    articleStatus:
                      data.articleStatus || brief.articleStatus || "",
                  },
                }));
              }
            })
            .catch((error) => {
              console.error("Error fetching brief status:", error);
            });
        }

        const subscription = subscribeToBriefStatus(
          brief.id,
          // Handle data
          (data) => {
            if (data.value?.data?.onBriefStatusChanged) {
              const statusData = data.value.data.onBriefStatusChanged;
              setBriefProgress((prev) => ({
                ...prev,
                [brief.id]: {
                  stepRemains: parseInt(
                    String(statusData.stepRemains || 0),
                    10
                  ),
                  articleStatus: statusData.articleStatus,
                },
              }));
              if (statusData.stepRemains === 0) {
                dispatch(
                  checkBriefStatus({
                    id: brief.id,
                  })
                );
              }
            }
          },
          // Handle error
          (error) => {},
          // Handle complete
          () => {}
        );

        if (subscription) {
          activeBriefSubscriptions.current.set(brief.id, subscription);
        }
      });
    }, 1000); // 1 second delay to ensure article subscriptions are established first

    // Clean up all subscriptions when component unmounts
    return () => {
      cleanupSubscriptions(activeSubscriptions.current);
      cleanupSubscriptions(activeBriefSubscriptions.current);
    };
  }, [tab, briefs, archivedBriefs]); // Updated dependencies to include tab

  const enchancedBriefs = briefs.map((brief) => {
    return {
      ...brief,
      stepRemains: briefProgress[brief.id]?.stepRemains ?? brief.stepRemains,
      articleStatus:
        briefProgress[brief.id]?.articleStatus || brief.articleStatus,
      articleProgress: articleProgress[brief.id],
      briefProgress: briefProgress[brief.id],
    };
  });

  const enchancedArchivedBriefs = archivedBriefs.map((brief) => {
    return {
      ...brief,
      stepRemains: briefProgress[brief.id]?.stepRemains || brief.stepRemains,
      articleStatus:
        briefProgress[brief.id]?.articleStatus || brief.articleStatus,
      articleProgress: articleProgress[brief.id],
      briefProgress: briefProgress[brief.id],
    };
  });

  // This function is no longer used as we're directly dispatching getArticle in the useEffect
  // Keeping it for reference or future use if needed
  const getArticleStatus = async (briefId: string) => {
    try {
      const result = await dispatch(getArticle({ id: briefId }));
      const data = result.payload?.data || {
        status: "not_generated",
        current: 0,
        total: 100,
        percentage: 0,
      };

      return data;
    } catch (error) {
      return {
        status: "not_generated",
        current: 0,
        total: 100,
        percentage: 0,
      };
    }
  };

  return (
    <>
      <Head>
        <title>Dashboard: Overview | SEOwind.io</title>
      </Head>
      <AddPaymentModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        location={router.pathname}
      />
      <DeleteDialog
        id={isDeleteDialogOpen}
        isOpen={Boolean(isDeleteDialogOpen)}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
      />
      {!isOnboardingShown && <Onboarding onClose={handleOnboardingClose} />}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ mb: 4 }}>
                <Grid container justifyContent="space-between" spacing={3}>
                  <Grid item>
                    <Typography variant="h5">
                      Your Content Briefs and Articles
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      // m: -1,
                      mb: 2,
                      flexDirection: "row",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        m: 1,
                      }}
                    >
                      {briefModalLoading ? (
                        <Button
                          sx={{ px: 7, py: 2 }}
                          size="large"
                          variant="contained"
                          disabled
                        >
                          <CircularProgress
                            size={12}
                            color="inherit"
                            sx={{
                              zIndex: 1,
                            }}
                          />
                        </Button>
                      ) : (
                        <Button
                          sx={{ borderRadius: "4px" }}
                          size="large"
                          variant="contained"
                          onClick={handleCreateBriefDialogOpen}
                        >
                          Create Article
                        </Button>
                      )}

                      {optimizeContentDialogLoading ? (
                        <Button
                          sx={{ m: 1, px: 7, py: 2 }}
                          size="large"
                          variant="contained"
                          disabled
                        >
                          <CircularProgress
                            size={12}
                            color="inherit"
                            sx={{
                              zIndex: 1,
                            }}
                          />
                        </Button>
                      ) : (
                        <Button
                          sx={{ m: 1 }}
                          size="large"
                          variant="outlined"
                          onClick={handleOptimizeContentDialogOpen}
                        >
                          Update Article
                        </Button>
                      )}
                    </Box>
                    {/* <Typography
                      variant="caption"
                      color="GrayText"
                      sx={{ mr: 1 }}
                    >
                      {currentPlan &&
                        `${leftBriefs} briefs left on your ${numberOfBriefs} briefs plan (resets in ${daysToReset} days)`}
                    </Typography> */}
                  </Grid>
                </Grid>
              </Box>
              <Grid container spacing={4}>
                <CreateBriefDialog
                  open={isBriefDialogOpen}
                  limit={briefLimit}
                  onClose={handleCloseBriefDialog}
                  onCreated={handleCreateBriefDialogOpen}
                  onOptimizeContent={handleOptimizeContentFromBrief}
                  isRedirect={false}
                />
              </Grid>
              <Grid container spacing={4}>
                <OptimizeContentDialog
                  open={isOptimizeContentDialogOpen}
                  limit={false}
                  onClose={handleCloseOptimizeContentDialog}
                  onCreated={handleOptimizeContentDialogOpen}
                  inputValues={optimizeContentPrefillData}
                  isRedirect={false}
                />
              </Grid>
              <Grid sx={{ mt: 2 }}>
                <Card sx={{ p: 3, overflow: "visible" }}>
                  <Box
                    sx={{ mb: 3, textAlign: "right" }}
                    display="flex"
                    justifyContent="space-between"
                  >
                    <Box display="flex" justifyContent="space-between">
                      {/* projects commented out */}
                      <SelectProjectDropdown
                        setCurrentProject={setCurrentProject}
                        tab={tab}
                        count={isArchiveTab ? archivedTotal : total}
                        isArticle={false}
                      />
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          width: "1px",
                          marginLeft: "6px",
                          marginRight: "16px",

                          height: "100%",
                          alignSelf: "center",
                          backgroundColor: "divider",
                        }}
                      />

                      <Tabs
                        onChange={handleTabChange}
                        aria-label="select tab"
                        textColor="inherit"
                        value={tab}
                      >
                        <Tab
                          label="Active"
                          value="active"
                          sx={{
                            fontSize: "16px",
                            opacity: 1,
                            ["&.Mui-selected"]: {
                              color: "primary.main",
                            },
                          }}
                        />
                        <Tab
                          label="Archived"
                          value="archived"
                          sx={{
                            fontSize: "16px",
                            opacity: 1,
                            ["&.Mui-selected"]: {
                              color: "primary.main",
                            },
                          }}
                        />
                      </Tabs>
                    </Box>
                    {/*projectDropdown  */}
                    <Box display="flex">
                      <TextField
                        defaultValue=""
                        placeholder="Search"
                        disabled={areBriefsLoading}
                        onChange={debouncedChangeHandler}
                      />
                    </Box>
                  </Box>

                  {tab === "active" ? (
                    <DashboardBriefTable
                      briefs={enchancedBriefs}
                      areBriefsLoading={areBriefsLoading}
                      total={total}
                      handlePageChange={handlePageChange}
                      handleRowsPerPageChange={handleRowsPerPageChange}
                      handleRetry={handleRetry}
                      page={page}
                      limit={activePageLimit}
                      tab={tab}
                      handleDelete={handleDelete}
                      handleRetryArticle={handleRetryArticle}
                      articleProgress={articleProgress}
                      briefProgress={briefProgress}
                    />
                  ) : (
                    <DashboardBriefTable
                      briefs={enchancedArchivedBriefs}
                      areBriefsLoading={areBriefsLoading}
                      total={archivedTotal}
                      handlePageChange={handlePageChange}
                      handleRowsPerPageChange={handleRowsPerPageChange}
                      handleRetry={handleRetry}
                      page={archivedPage}
                      limit={archivedPageLimit}
                      tab={tab}
                      handleDelete={handleDelete}
                      handleRetryArticle={handleRetryArticle}
                      articleProgress={articleProgress}
                      briefProgress={briefProgress}
                    />
                  )}
                </Card>
              </Grid>
            </Grid>
            <Grid item xs={4}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: "1.5rem" }}>
                  Product tour
                </Typography>
                <iframe
                  style={{ aspectRatio: " 16 / 9", width: "100%" }}
                  id="ytplayer"
                  height="auto"
                  src="https://www.youtube.com/embed/zFwul9Fs1tY"
                  frameBorder="0"
                  allowFullScreen
                ></iframe>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

Overview.getLayout = (page) => (
  <AuthGuard>
    <DashboardLayout>{page}</DashboardLayout>
  </AuthGuard>
);

export default Overview;
